import React from 'react';
import type { FC, FormEvent } from 'react';
import { useRef, useState } from 'react';
import { CheckIcon, CrossIcon, EyeIcon, EyeOffIcon, LockIcon, MailIcon } from '../../assets/icons/icons';
import styles from './AuthPanel.module.scss';
import { MAX_URL } from '../../data/config';

export interface Credentials {
    email: string;
    password: string;
    remember: boolean;
}

interface AuthPanelProps {
    onSubmit: (credentials: Credentials) => void;
    submitting?: boolean;
    onForgotPassword: () => void;
}

export const AuthPanel: FC<AuthPanelProps> = ({
    onSubmit,
    submitting = false,
    onForgotPassword,
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const emailRef = useRef<HTMLInputElement>(null);

    const handleClearEmail = (): void => {
        setEmail('');
        setPassword('');
        setPasswordVisible(false);
        emailRef.current?.focus();
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        onSubmit({ email, password, remember });
    };

    return (
        <section className={styles.panel}>
            <div className={styles.main}>
                <h2 className={styles.heading}>Добро пожаловать!</h2>

                <p className={styles.description}>
                    Введите логин и пароль, чтобы продолжить работу в сервисе.
                </p>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <label className={styles.fieldWrap}>
                        <span className={styles.fieldLabel}>Логин</span>

                        <span className={styles.field}>
                            <span className={styles.fieldIcon}>
                                <MailIcon />
                            </span>

                            <input
                                ref={emailRef}
                                type="text"
                                inputMode="email"
                                autoComplete="username"
                                placeholder="name@yanao.ru"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                required
                            />

                            {email && (
                                <button
                                    type="button"
                                    className={styles.fieldAction}
                                    onClick={handleClearEmail}
                                    aria-label="Очистить логин"
                                >
                                    <CrossIcon />
                                </button>
                            )}
                        </span>
                    </label>

                    <label className={styles.fieldWrap}>
                        <span className={styles.fieldLabel}>Пароль</span>

                        <span className={styles.field}>
                            <span className={styles.fieldIcon}>
                                <LockIcon />
                            </span>

                            <input
                                type={passwordVisible ? 'text' : 'password'}
                                autoComplete="current-password"
                                placeholder="Введите пароль"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                required
                            />

                            <button
                                type="button"
                                className={styles.fieldAction}
                                onClick={() => setPasswordVisible((visible) => !visible)}
                                aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
                            >
                                {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </span>
                    </label>

                    <div className={styles.formRow}>
                        <label className={styles.remember}>
                            <input
                                type="checkbox"
                                checked={remember}
                                onChange={(event) => setRemember(event.target.checked)}
                            />

                            <span className={styles.checkmark}>
                                <CheckIcon />
                            </span>

                            <span>Запомнить меня</span>
                        </label>

                        <button type="button" className={styles.forgot} onClick={onForgotPassword}>
                            Забыли пароль?
                        </button>
                    </div>

                    <button type="submit" className={styles.submit} disabled={submitting}>
                        {submitting ? 'Вход...' : 'Войти в систему'}
                    </button>
                </form>

                <div className={styles.help}>
                    <span>Возникли проблемы со входом?</span>
                    <a className={styles.forgot} href={MAX_URL} target="_blank" rel="noopener noreferrer">
                        Обратиться в техподдержку ↗
                    </a>
                </div>

                <span className={styles.hours}>Часы работы: по будням с 09:00 до 18:00 (МСК)</span>

                <div className={styles.footnote}>
                    Доступ только для авторизованных пользователей сервиса
                </div>
            </div>
        </section>
    );
};